import { MutationFunction } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { container, ContainerType } from "@/app/container";
import { getSupportEmail } from "@/app/lib/get-support-email";
import {
  BeginDocumentUploadV2Mutation,
  BeginDocumentUploadV2MutationVariables,
  Document,
  DocumentType,
  EndDocumentUploadMutation,
  EndDocumentUploadMutationVariables,
  useBeginDocumentUploadV2Mutation,
  useEndDocumentUploadMutation,
} from "@/app/types/generated/graphql";

import { DocUploadProps } from "./doc-upload.types";

export const requiredDocAcceptTypeMap: Record<DocumentType, string> = {
  ID_CARD: "image/png, image/jpeg, image/jpg",
  OTHER: "image/png, image/jpeg, image/jpg, application/pdf",
  PASSPORT: "image/png, image/jpeg, image/jpg",
  LICENSE: "image/png, image/jpeg, image/jpg",
};

/**
 * File size limit for document uploads set to 10 MB
 */
export const FILE_SIZE_LIMIT_IN_MB = 10;

const getFileSizeInMB = (file: File) => {
  return file.size / 1024 / 1024;
};

export const performDocumentUpload = async (
  selectedFile: File,
  requiredDocument: Document,
  docType: string,
  uploadFileApi: ContainerType["api"],
  beginDocumentUploadV2: MutationFunction<BeginDocumentUploadV2Mutation, BeginDocumentUploadV2MutationVariables>,
  endDocumentUpload: MutationFunction<EndDocumentUploadMutation, EndDocumentUploadMutationVariables>,
  onFinish: () => void,
) => {
  const { data } = await beginDocumentUploadV2({
    variables: {
      document: {
        id: requiredDocument?.id,
        fileName: selectedFile.name,
        mediaType: selectedFile.type,
        size: selectedFile.size,
        type: docType as DocumentType,
      },
    },
  });

  const { url, id: documentId } = data?.beginDocumentUploadV2 || {};

  await uploadFileApi.put(url || "", selectedFile, {
    headers: {
      "Content-Type": selectedFile.type,
    },
  });

  const delayDuration = getFileSizeInMB(selectedFile) * 3000; // Delay the request by 3 seconds per MB; larger the file size, larger the delay
  await new Promise((resolve, reject) => {
    setTimeout(
      async () => {
        try {
          await endDocumentUpload({ variables: { documentId } });
          resolve(true);
        } catch (error) {
          reject(error);
        }
      },
      delayDuration < 5000 ? 5000 : delayDuration, // delay the request by a minimum of 5 seconds
    );
  });

  onFinish();
};

export const readableFileTypes = (acceptedTypeList: string[]) => {
  const formattedTypes = acceptedTypeList.map((type) => type.split("/")[1].toUpperCase());
  if (formattedTypes.length === 1) return formattedTypes[0];
  if (formattedTypes.length === 2) return formattedTypes.join(" or ");
  return `${formattedTypes.slice(0, -1).join(", ")}, or ${formattedTypes.at(-1)}`;
};

export const useDocUpload = ({ requiredDocument, onFinish }: Omit<DocUploadProps, "isUSPerson">) => {
  const { api } = container;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File>();
  const supportEmail = getSupportEmail();

  const [beginDocumentUploadV2] = useBeginDocumentUploadV2Mutation();
  const [endDocumentUpload] = useEndDocumentUploadMutation({
    refetchQueries: ["Account"],
  });

  const [docType, setDocType] = useState<DocumentType | undefined>(requiredDocument?.type);

  useEffect(() => {
    if (!requiredDocument || !selectedFile) return;
    const acceptedTypes: string = requiredDocAcceptTypeMap[requiredDocument.type] ?? "";
    const acceptedTypeList = acceptedTypes.split(",").map((type) => type.trim());

    const isFileTypeValid = acceptedTypeList.includes(selectedFile.type);

    if (!isFileTypeValid) {
      setError(`Unsupported file type selected. Only ${readableFileTypes(acceptedTypeList)} files are allowed.`);
      setSelectedFile(undefined);
    } else if (getFileSizeInMB(selectedFile) > FILE_SIZE_LIMIT_IN_MB) {
      setError(
        `Your document size is a little large, we can only handle a maximum size of ${FILE_SIZE_LIMIT_IN_MB} MB`,
      );
    } else {
      setError("");
    }
  }, [requiredDocument, selectedFile]);

  const uploadDocument = useCallback(async () => {
    if (!selectedFile || !requiredDocument) return;

    try {
      setLoading(true);
      await performDocumentUpload(
        selectedFile,
        requiredDocument,
        docType as DocumentType,
        api,
        beginDocumentUploadV2,
        endDocumentUpload,
        onFinish,
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        const message =
          t(error?.message) ||
          t(`Something went wrong, please try again later or contact us at {{supportEmail}}`, { supportEmail });
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [
    selectedFile,
    beginDocumentUploadV2,
    docType,
    requiredDocument,
    api,
    t,
    endDocumentUpload,
    onFinish,
    supportEmail,
  ]);

  return {
    setSelectedFile,
    docType,
    setDocType,
    loading,
    selectedFile,
    uploadDocument,
    error,
  };
};
